import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { settingsApi } from '@/services/settings.service';
import { uploadImage } from '@/services/upload.service';
import type { PlatformSettings } from '@/types/settings.types';
import { settingsFormSchema } from '@/validators/settings.validator';

const SETTINGS_QUERY_KEY = 'settings';

export interface SettingsFormValues {
  commissionPct: string;
  gstPct: string;
  settlementFeePct: string;
  upiId: string;
  payeeName: string;
  qrImageUrl: string;
  bankNote: string;
}

type FieldErrors = Partial<Record<keyof SettingsFormValues, string>>;

export interface UseSettingsPageResult {
  form: SettingsFormValues;
  errors: FieldErrors;
  isLoading: boolean;
  isSaving: boolean;
  isUploadingQr: boolean;
  uploadError: string | null;
  submitError: string | null;
  successMessage: string | null;
  onChange: (field: keyof SettingsFormValues, value: string) => void;
  onUploadQrImage: (file: File) => void;
  onClearQrImage: () => void;
  onSubmit: () => void;
}

const EMPTY_FORM: SettingsFormValues = {
  commissionPct: '',
  gstPct: '',
  settlementFeePct: '',
  upiId: '',
  payeeName: '',
  qrImageUrl: '',
  bankNote: '',
};

const toForm = (s: PlatformSettings): SettingsFormValues => ({
  commissionPct: String(s.fees.commissionPct),
  gstPct: String(s.fees.gstPct),
  settlementFeePct: String(s.fees.settlementFeePct),
  upiId: s.payment.upiId,
  payeeName: s.payment.payeeName,
  qrImageUrl: s.payment.qrImageUrl,
  bankNote: s.payment.bankNote,
});

export const useSettingsPage = (): UseSettingsPageResult => {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<SettingsFormValues>(EMPTY_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUploadingQr, setIsUploadingQr] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const settingsQuery = useQuery({
    queryKey: [SETTINGS_QUERY_KEY],
    queryFn: () => settingsApi.get(),
  });

  // Seed the form once, the first time settings arrive — later saves update via setQueryData.
  useEffect(() => {
    if (settingsQuery.data && !hydrated) {
      setForm(toForm(settingsQuery.data));
      setHydrated(true);
    }
  }, [settingsQuery.data, hydrated]);

  const updateMutation = useMutation({
    mutationFn: (body: PlatformSettings) => settingsApi.update(body),
    onSuccess: (saved) => {
      queryClient.setQueryData([SETTINGS_QUERY_KEY], saved);
      setForm(toForm(saved));
      setSuccessMessage('Settings saved');
    },
    onError: (error: unknown) => {
      setSubmitError(error instanceof Error ? error.message : 'Could not save settings');
    },
  });

  const onChange = (field: keyof SettingsFormValues, value: string): void => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setSubmitError(null);
    setSuccessMessage(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onUploadQrImage = (file: File): void => {
    setUploadError(null);
    setSuccessMessage(null);
    setIsUploadingQr(true);
    uploadImage('qr-codes', file)
      .then((url) => setForm((prev) => ({ ...prev, qrImageUrl: url })))
      .catch(() => setUploadError('Could not upload the image. Try again.'))
      .finally(() => setIsUploadingQr(false));
  };

  const onClearQrImage = (): void => {
    setUploadError(null);
    setForm((prev) => ({ ...prev, qrImageUrl: '' }));
  };

  const onSubmit = (): void => {
    setSubmitError(null);
    setSuccessMessage(null);

    const parsed = settingsFormSchema.safeParse(form);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof SettingsFormValues;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});

    updateMutation.mutate({
      fees: {
        commissionPct: parsed.data.commissionPct,
        gstPct: parsed.data.gstPct,
        settlementFeePct: parsed.data.settlementFeePct,
      },
      payment: {
        upiId: parsed.data.upiId,
        payeeName: parsed.data.payeeName,
        qrImageUrl: parsed.data.qrImageUrl,
        bankNote: parsed.data.bankNote,
      },
    });
  };

  return {
    form,
    errors,
    isLoading: settingsQuery.isLoading,
    isSaving: updateMutation.isPending,
    isUploadingQr,
    uploadError,
    submitError,
    successMessage,
    onChange,
    onUploadQrImage,
    onClearQrImage,
    onSubmit,
  };
};
